import { useState } from "react";
import { hapticTasbeeh } from "../haptics";

// Tasbeeh — Dhikr counter component
// Cycles through 3 phases of 33: SubhanAllah → Alhamdulillah → Allahu Akbar
// State is persisted in localStorage so counts survive page refreshes

const DHIKR = [
  { name: "SubhanAllah",   arabic: "سُبْحَانَ اللَّهِ",   meaning: "Glory be to Allah",          accent: "#00c9a7", glow: "rgba(0,201,167,0.35)" },
  { name: "Alhamdulillah", arabic: "الْحَمْدُ لِلَّهِ",   meaning: "All praise is due to Allah", accent: "#ffb300", glow: "rgba(255,179,0,0.35)" },
  { name: "Allahu Akbar",  arabic: "اللَّهُ أَكْبَرُ",    meaning: "Allah is the Greatest",      accent: "#a078ff", glow: "rgba(160,120,255,0.35)" },
];

const ARABIC_NUMS = ["٠","١","٢","٣","٤","٥","٦","٧","٨","٩"];
const toArabicNum = n => String(n).split("").map(c => ARABIC_NUMS[parseInt(c)] || c).join("");

export default function Tasbeeh({ darkMode }) {
  const [tasbeeh, setTasbeeh] = useState(() => parseInt(localStorage.getItem("tasbeeh") || "0"));
  const [tasbeehSet, setTasbeehSet] = useState(() => parseInt(localStorage.getItem("tasbeehSet") || "0"));

  const tapTasbeeh = () => {
    const next = tasbeeh + 1;
    setTasbeeh(next);
    localStorage.setItem("tasbeeh", next);
    // Haptics: light on every tap, medium at 33/66/99, heavy at 100 (set complete)
    hapticTasbeeh(next);
    if (next % 100 === 0) {
      setTasbeehSet(s => { const ns = s + 1; localStorage.setItem("tasbeehSet", ns); return ns; });
    }
  };

  const resetTasbeeh = () => {
    setTasbeeh(0);
    setTasbeehSet(0);
    localStorage.setItem("tasbeeh", 0);
    localStorage.setItem("tasbeehSet", 0);
  };

  const phase   = Math.floor(tasbeeh / 33) % 3;
  const countIn = tasbeeh % 33;
  const d       = DHIKR[phase];
  const pct     = countIn / 33;
  const ringSize = Math.min(window.innerWidth - 60, 380);
  const rSvg    = (ringSize - 48) / 2;
  const circ    = 2 * Math.PI * rSvg;
  const setsOf99 = Math.floor(tasbeeh / 99);
  const bgGradient = darkMode
    ? `radial-gradient(ellipse at 50% 30%, ${d.accent}12 0%, transparent 70%)`
    : `radial-gradient(ellipse at 50% 30%, ${d.accent}08 0%, transparent 70%)`;

  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",padding:"6px 20px 20px",gap:"0",textAlign:"center",position:"relative",overflow:"hidden",minHeight:"100%",background:bgGradient,transition:"background 0.6s ease"}}>

      {/* Dhikr text */}
      <div style={{marginBottom:"0",zIndex:1}}>
        <div style={{fontSize:"36px",fontWeight:"900",color:d.accent,direction:"rtl",letterSpacing:"2px",lineHeight:1.4,fontFamily:"'Scheherazade New', 'Amiri', serif",marginBottom:"4px",textShadow:`0 0 30px ${d.glow}`,transition:"color 0.4s, text-shadow 0.4s"}}>{d.arabic}</div>
        <div style={{fontSize:"17px",fontWeight:"800",color:d.accent,marginBottom:"2px",transition:"color 0.4s"}}>{d.name}</div>
        <div style={{fontSize:"11px",color:"var(--muted)",fontStyle:"italic",letterSpacing:"0.5px"}}>{d.meaning}</div>
      </div>

      {/* Phase indicator pills */}
      <div style={{display:"flex",gap:"6px",margin:"8px 0 4px",zIndex:1}}>
        {DHIKR.map((dh, i) => (
          <div key={i} style={{
            height:"6px", borderRadius:"3px",
            width: i === phase ? "28px" : "10px",
            background: i === phase ? dh.accent : (darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"),
            transition: "all 0.4s ease",
            boxShadow: i === phase ? `0 0 12px ${dh.accent}` : "none"
          }}/>
        ))}
      </div>

      {/* SVG ring tap target */}
      <div
        onClick={(e) => {
          tapTasbeeh();
          const rect = e.currentTarget.getBoundingClientRect();
          const cx = e.clientX - rect.left - ringSize / 2;
          const cy = e.clientY - rect.top - ringSize / 2;
          const el = e.currentTarget;
          for (let i = 0; i < 6; i++) {
            const p = document.createElement("div");
            const angle = (Math.PI * 2 * i) / 6;
            const dist = 40 + Math.random() * 30;
            p.style.cssText = `position:absolute;width:5px;height:5px;border-radius:50%;background:${d.accent};left:50%;top:50%;margin-left:${cx}px;margin-top:${cy}px;pointer-events:none;z-index:5;--tx:${Math.cos(angle)*dist}px;--ty:${Math.sin(angle)*dist}px;animation:tasbeehParticle 0.6s ease-out forwards;`;
            el.appendChild(p);
            setTimeout(() => p.remove(), 650);
          }
        }}
        style={{position:"relative",width:`${ringSize}px`,height:`${ringSize}px`,flexShrink:0,cursor:"pointer",WebkitTapHighlightColor:"transparent",userSelect:"none",zIndex:1,animation:"tasbeehPulse 0.15s ease-out","--tasbeeh-glow":d.glow}}
      >
        <svg width={ringSize} height={ringSize} style={{position:"absolute",inset:0,overflow:"visible",animation:"tasbeehRingGlow 3s ease-in-out infinite","--tasbeeh-glow":d.glow}}>
          <circle cx={ringSize/2} cy={ringSize/2} r={rSvg} fill="none" stroke={darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)"} strokeWidth="16"/>
          <circle cx={ringSize/2} cy={ringSize/2} r={rSvg} fill="none"
            stroke={d.accent} strokeWidth="16"
            strokeDasharray={`${pct*circ} ${circ}`}
            strokeLinecap="round"
            transform={`rotate(-90 ${ringSize/2} ${ringSize/2})`}
            style={{transition:"stroke-dasharray 0.2s ease, stroke 0.4s ease"}}
          />
          {[0, 11, 22].map((mark, mi) => {
            const angle = ((mark / 33) * 360 - 90) * (Math.PI / 180);
            const dotX = ringSize/2 + rSvg * Math.cos(angle);
            const dotY = ringSize/2 + rSvg * Math.sin(angle);
            return <circle key={mi} cx={dotX} cy={dotY} r="3" fill={mark < countIn ? d.accent : (darkMode?"rgba(255,255,255,0.15)":"rgba(0,0,0,0.12)")} style={{transition:"fill 0.3s"}}/>;
          })}
        </svg>
        <div style={{
          position:"absolute",inset:"24px",borderRadius:"50%",
          background:darkMode?`radial-gradient(circle at 40% 30%, ${d.accent}18, #07090c 75%)`:`radial-gradient(circle at 40% 30%, ${d.accent}10, #f5f5f5 75%)`,
          border:`1.5px solid ${d.accent}30`,
          display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
          boxShadow:`0 16px 50px rgba(0,0,0,${darkMode?0.5:0.15}), inset 0 1px 0 rgba(255,255,255,${darkMode?0.06:0.4})`,
          transition:"background 0.4s, border-color 0.4s, box-shadow 0.4s",
        }}>
          <div style={{fontSize:"68px",fontWeight:"900",color:d.accent,lineHeight:1,fontVariantNumeric:"tabular-nums",letterSpacing:"-3px",transition:"color 0.4s"}}>{countIn}</div>
          <div style={{fontSize:"22px",color:d.accent,opacity:0.5,fontFamily:"'Scheherazade New', 'Amiri', serif",direction:"rtl",marginTop:"-2px",transition:"color 0.4s"}}>{toArabicNum(countIn)}</div>
          <div style={{fontSize:"9px",fontWeight:"800",color:"var(--muted)",letterSpacing:"3px",textTransform:"uppercase",marginTop:"2px"}}>of 33</div>
        </div>
      </div>

      {/* Tap hint */}
      <div style={{fontSize:"10px",color:"var(--muted)",opacity:0.5,marginTop:"-4px",marginBottom:"8px",zIndex:1}}>Tap the circle to count</div>

      {/* Stats cards */}
      <div style={{display:"flex",gap:"12px",margin:"4px 0 12px",zIndex:1}}>
        {[
          {v: tasbeeh, label: "TOTAL"},
          {v: setsOf99, label: "SETS OF 99"}
        ].map(({v, label}) => (
          <div key={label} style={{textAlign:"center",padding:"12px 22px",background:darkMode?`linear-gradient(135deg, ${d.accent}12, ${d.accent}05)`:`linear-gradient(135deg, ${d.accent}10, ${d.accent}03)`,border:`1px solid ${d.accent}20`,borderRadius:"16px",minWidth:"95px",transition:"background 0.4s, border-color 0.4s"}}>
            <div style={{fontSize:"24px",fontWeight:"800",color:d.accent,transition:"color 0.4s"}}>{v}</div>
            <div style={{fontSize:"8px",color:"var(--muted)",fontWeight:"700",letterSpacing:"1.5px",marginTop:"4px"}}>{label}</div>
          </div>
        ))}
      </div>

      {/* Reset button */}
      <button onClick={resetTasbeeh} style={{padding:"10px 32px",background:"transparent",border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.1)"}`,borderRadius:"20px",color:"var(--muted)",fontSize:"12px",fontWeight:"600",cursor:"pointer",fontFamily:"inherit",letterSpacing:"1px",textTransform:"uppercase",zIndex:1,marginTop:"auto"}}>Reset</button>
    </div>
  );
}
